import moment from 'moment'
import {isEqual} from  'lodash'
import {compile} from 'path-to-regexp'
import {resolve, reduce} from 'bluebird'
import React, {useState, useMemo} from 'react'
import {DndProvider} from 'react-dnd'
import HTML5Backend from 'react-dnd-html5-backend'
import {Switch, Route, Redirect} from 'react-router-dom'
import Text from 'react-text'
import Topbar from './components/topbar'
import Navigation from './components/navigation'
import Menu from './components/menu'
import Page from './components/page'
import {teacherPages} from './roles/teacher/index'
import PlanningPdf from './components/planning-pdf'
import roles from './roles'
import queryString from 'query-string'
import {sprintf} from 'sprintf-js'
import Chat from './components/chat'
import Context, {Consumer} from './context'
import * as initializers from './helpers/api' 
import theme, { ThemeProvider, StyledEngineProvider, LegacyThemeProvider } from 'themes';
import { getProfile } from 'helpers/users'
import KeepAliveForm from 'components/KeepAliveForm'

function searchElement (url) {
  const index = url.indexOf('?')
  return index < 0 ? '' : url.substr(index + 1)
}

function getQueryString (query, newQuery, clear) {
  const string = queryString.stringify(clear ? newQuery : {...query, ...newQuery})
  return string == '' ? '' : `?${string}`
}

function init (context, [name, fetch]) {
  return resolve(fetch).then(data => ({...context, [name]: data}))
}

const initContext = reduce(Object.entries(initializers), init, {})

export default async function App () {
  const context = await initContext
  const {languages, profile, settings} = context
  const matchLanguage = (language) => languages.find(({id}) => id == language)

  const {last_language: lastLanguage, role} = profile.Passport 
  const {project, premium, noChat, locked = false, lockedMessage = {date: '2024-01-10', title: 'Aviso', message: 'Locked'}} = profile.institution
  const loadChat = premium
  const defaultPage = settings.defaults[project][role].page

  const defaultRedirect = ({match: {params: {language: id, page}}}) => {
    const language = matchLanguage(id)
    const url =  page || defaultPage
    const link =`/${(language || lastLanguage).id}/${role}/${url}`
    return (<Redirect to={link}/>)
  }

  const App = ({match, history}) => {
    
    const [profile, setProfile] = useState(context.profile)
    
    const refreshProfile = () => {
      getProfile().then(([newProfile]) => {
        setProfile({...profile, ...newProfile})
      })  
    }

    const query = queryString.parse(searchElement(location.hash))
    const language = matchLanguage(match.params.languageId)
    moment.locale(language.id)

    const link = (params, path = match.path, options = {}) => {
      const buildPath = compile(path)
      let linkPath = buildPath(options.override ? params : ({...match.params, ...params}))
      let linkQuery = options.preserveQuery
        ? {...query, ...(options.query || {})}
        : options.query
      if (!isEqual(linkQuery, {})) {
        if (linkQuery)
          linkPath = `${linkPath}?${queryString.stringify(linkQuery)}`
        else if (options.preserveQuery)
          linkPath = `${linkPath}?${queryString.stringify(linkQuery)}`
      }
      return linkPath.replace(/\?$/,'');
    }

    const linkQuery = (link, query, clearQuery) => `${link}${getQueryString(props.query, query, clearQuery)}`

    const navigate = (params, path = match.path, options) => {
      const pathname = link(params, path, options)
      if (location.hash.substring(1) != pathname)
        history.push(pathname)
    }

    const translate = (key, params) => {
      const translations = context.translations[key] || {}
      const translation = translations[match.params.languageId]
      if (translation) return params ? sprintf(translation, params) : translation
      else return key
    }

    const props = {
      ...context,
      profile,
      locked,
      lockedMessage,
      refreshProfile,
      ...match.params,
      translate,
      query,
      link,
      linkQuery,
      navigate,
      match,
      history,
      language,
      defaultRedirect: defaultRedirect({match})
    }

    const page = useMemo(()=> {
      return match.params.page || 'dashboard'
    }, [match.params])

    const TeacherPage = useMemo(() => {
      return teacherPages[page] || teacherPages['dashboard']
    }, [page])

    return (
      <Context {...props}>
        <Consumer>
          {({languageId, translations, role, page, sizes, setState}) => {

            /*
            if (converse && converse.saveWindowState) {
              converse.saveWindowState({type: page == 'chat' ? 'focus' : 'blur'})
            }
            */

            /*
            if (page == 'chat' && !loadChat) return setTimeout(() => navigate({page: 'premium-required'}, '/:languageId/:role/:page'), 0)
            */

            if (page == 'planning-pdf') return (
              <Text language={languageId} dictionary={translations}>
                <PlanningPdf />
              </Text>
            )

            else return (roles[role] && roles[role][page]) || page == 'chat'
              ? (<Text language={languageId} dictionary={translations}>
                  <StyledEngineProvider injectFirst>
                  <LegacyThemeProvider theme={theme}>
                    <ThemeProvider theme={theme}>
                        <DndProvider backend={HTML5Backend}>
                          <Topbar />
                          {!locked && <Menu />}
                          <Navigation />
                          <KeepAliveForm />
                          {!noChat && (<Chat
                            width={sizes.width}
                            pageName={page}
                            style={{display: page == 'chat' ? 'flex' : 'none', 'flexGrow': 1, width: `${sizes.width}px`}}
                          />)}
                          {page != 'chat' && (<div className={`page ${page}`}> <TeacherPage /></div>)}
                        </DndProvider>
                      </ThemeProvider>
                    </LegacyThemeProvider>
                  </StyledEngineProvider>
                </Text>)
              : (<Redirect to={defaultRedirect}/>);
          }}
        </Consumer>
      </Context>
    );
  }
  const uuid = '[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}'
  return () => (
    <Switch>
      <Route exact path='/' render={defaultRedirect}/>
      <Route exact path='/:languageId' render = {defaultRedirect} />
      <Route exact path='/:languageId/:role' render = {defaultRedirect} />
      <Route exact path='/:languageId/:role/:page(my-courses)/:course(C...G..)?/:unit(\d\d)?' component = {App} />
      <Route exact path='/:languageId/:role/:page(my-courses)/:course(C...G..)?/:unit(\d\d)?/:action(schedule|posts)/:lesson(C...G..U\d\dL\d\d)' component = {App} />
      <Route exact path='/:languageId/:role/:page(dashboard)' component = {App} />
      <Route exact path='/:languageId/:role/:page(catalogue)/:subject(C.)?/:course(C...G..)?/:unit(\d\d)?' component= {App} />
      <Route exact path='/:languageId/:role/:page(catalogue)/:subject(C.)?/:course(C...G..)?/:unit(\d\d)?/:action(schedule|posts)/:lesson(C...G..U\d\dL\d\d)' component = {App} />
      <Route exact path='/:languageId/:role/:page(catalogue)/:subject(C.)?/:searching(lessons)' component = {App} />
      <Route exact path='/:languageId/:role/:page(catalogue)/:subject(C.)?/:searching(lessons)?/:action(schedule|posts)/:lesson(C...G..U\d\dL\d\d)' component = {App} />
      <Route exact path='/:languageId/:role/:page(monitoring)/:section(groups)?' component = {App} />
      <Route exact path={`/:languageId/:role/:page(monitoring)/:groupId(${uuid})?/:section(students)`} component = {App} />
      <Route exact path={`/:languageId/:role/:page(monitoring)/:groupId(${uuid})?/:section(student)/:studentId(${uuid})`} component = {App} />
      <Route exact path='/:languageId/:role/:page(calendars)/:view(month|day|week)?/:action(comment)?' component = {App} />
      <Route exact path={`/:languageId/:role/:page(calendars)/:view(day)/:action(schedule|posts)/:event(${uuid})`} component = {App} />
      <Route exact path='/:languageId/:role/:page(my-lessons)' component = {App} />
      <Route exact path={`/:languageId/:role/:page(my-lessons)/:action(schedule|posts)/:upload(${uuid})`} component = {App} />
      <Route exact path={`/:languageId/:role/:page(planning|planning-rd)/:view(month|week|day)?/:action(pdf|duplicate)?/:planId(${uuid})?`} component = {App} />
      <Route exact path={`/:languageId/:role/:page(planning|planning-rd)/:view(plan|plan-rd)/:planId(${uuid})?/:section(templates|add-lessons|add-standards|add-link-resource)?/:activeTab(catalogue|my-lessons)?`} component = {App} />
      <Route exact path={`/:languageId/:role/:page(planning|planning-rd)/:view(preview|preview-rd)/:planId(${uuid})`} component = {App} />
      <Route exact path={`/:languageId/:role/:page(planning-pdf)/:group(${uuid})?`} component = {App} />
      <Route exact path={`/:languageId/:role/:page(resources)`} component = {App} />
      <Route exact path={`/:languageId/:role/:page(help)`} component = {App} />
      <Route exact path={`/:languageId/:role(admin)/:page(groups)`} component = {App} />
      <Route exact path={`/:languageId/:role/:page(chat)`} component = {App} />
      <Route exact path={`/:languageId/:role/:page(premium-required)`} component = {App} />

      <Route exact path={`/:languageId/:role/:page(rollbook)`} component = {App} />
      <Route exact path={`/:languageId/:role/:page(gradebook)/:groupId(${uuid})?/:studentId(${uuid})?`} component = {App} />
      
      <Route exact path={`/:languageId/:role/:page(homework)/:homeworkId(${uuid})?`} component={App} />

      <Route exact path={`/:languageId/:role/:page(assessment)`} component = {App} />
      <Route exact path={`/:languageId/:role/:page(assessment)/:action(schedule)/:assessment(${uuid})`} component = {App} />

      <Route exact path={`/:languageId/:role/quiz`} render={props => {
        const {languageId, role} = props.match.params
        return <Redirect to={`/${languageId}/${role}/quiz/assessments`}/>
       }} />
      <Route exact path={`/:languageId/:role/:page(quiz)/imported-test/:attemptId(${uuid})`} component={App} />
      <Route exact path={`/:languageId/:role/:page(quiz)/:action(schedule)/:assessment(${uuid})`} component = {App} />
      <Route exact path={`/:languageId/:role/:page(quiz)/:tab(assessments|question-bank|assessment-bank|assigned)`} component = {App} />
      <Route exact path={`/:languageId/:role/:page(quiz)/:tab(assessments|question-bank|assessment-bank|assigned)/:sectionName(standards-search)`} component={App} />
      <Route exact path={`/:languageId/:role/:page(quiz)/:tab(assessments|question-bank|assessment-bank|assigned)/:assessment(${uuid})/:sectionName(standards)`} component={App} />
      <Route exact path={`/:languageId/:role/:page(quiz)/:tab(assigned)/:assignmentId(${uuid})`} component = {App} />
      <Route exact path={`/:languageId/:role/:page(quiz)/:tab(question-bank)/:sectionName(edit-question|new-question)/:questionId(${uuid})/:operation(add-standards)?`} component={App} />
      <Route exact path={`/:languageId/:role/:page(quiz)/:assessment(${uuid})/:action(edit|preview)/:sectionName(standards)`} component={App} />
      <Route exact path={`/:languageId/:role/:page(quiz)/:assessment(${uuid})/:action(edit|preview|review|take|preview-results|standards-results)/:sectionId(${uuid})?/:questionId(${uuid})?/:operation(add-standards)?`} component = {App} />
      <Route exact path={`/:languageId/:role/:page(quiz)/:assessment(${uuid})/:action(edit)/:sectionId(${uuid})?/:questionId(${uuid})?/:sectionName(standards-search)`} component={App} />

      <Route exact path={`/:languageId/:role/:page(grading)/:section(create-school-year|books|book)?`} component = {App} />
      <Route exact path={`/:languageId/:role/:page(grading)/:section(book)/:bookId(${uuid})`} component = {App} />

      <Route exact path={`/:languageId/:role/:page(videos)`} component = {App} />

    </Switch>
  )
}
